<template>
  <div :class="componentWrapperClassName" class="book-meta-tabs">
    <!-- Tab Buttons -->
    <div class="book-meta-tabs-controls">
      <div ref="tabButtons" class="tab-buttons">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :ref="`tab-${tab.id}`"
          :class="[
            'tab-button',
            { 'tab-button--active': activeTab === tab.id }
          ]"
          :disabled="isButtonsDisabled"
          @click="handleTabChange(tab.id)"
        >
          {{ isNarrow ? tab.shortTitle : tab.title }}
        </button>
        <!-- Animated underline -->
        <div
          ref="underline"
          class="tab-underline"
          :style="underlineStyle"
        ></div>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="tab-content mt-4">
      <!-- Book Tab - empty, actual book content is displayed separately -->
      <div v-show="activeTab === 'book'" class="book-block">
        <!-- Empty content - book text is displayed in parent component -->
      </div>

      <!-- Brief Introduction Tab -->
      <div v-show="activeTab === 'brief-intro'" class="tab-content-placeholder">
        <div v-html="bookSeo.briefIntroduction"></div>
      </div>

      <!-- About Author Tab -->
      <div
        v-show="activeTab === 'about-author'"
        class="tab-content-placeholder"
      >
        <div v-html="bookSeo.aboutAuthor"></div>
      </div>

      <!-- F.A.Q. Tab -->
      <div v-show="activeTab === 'faq'" class="tab-content-placeholder">
        <v-expansion-panels flat tile>
          <v-expansion-panel
            v-for="(qa, qaIndex) in bookSeo.FAQ"
            :key="qaIndex"
            class="faq-item-block"
          >
            <v-expansion-panel-header>
              {{ qa.question }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              {{ qa.answer }}
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('BookMetaTabs.vue');

export default {
  name: 'BookMetaTabs',

  props: {
    metaView: {
      type: Object,
      required: true
    },
    bookSeo: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      activeTab: 'book',
      underlineStyle: {
        width: '0px',
        left: '0px'
      },
      componentWrapperClassName: AppConstantsUtil.META_BLOCK_TABS_CLASS
    };
  },
  computed: {
    ...mapGetters('MediaDetectorStore', ['mediaSize', 'additionalMediaSize']),
    isNarrow() {
      const { narrow } = this.mediaSize;
      return narrow;
    },
    tabs() {
      const tabs = [
        {
          id: 'book',
          title: this.$t('MetaTabsBlock.tabs.book.label'),
          shortTitle: this.$t('MetaTabsBlock.tabs.book.label')
        }
      ];
      if (this.bookSeo.briefIntroduction) {
        tabs.push({
          id: 'brief-intro',
          title: this.$t('MetaTabsBlock.tabs.brief-intro.label'),
          shortTitle: this.$t('MetaTabsBlock.tabs.brief-intro.short.label')
        });
      }
      if (this.bookSeo.aboutAuthor) {
        tabs.push({
          id: 'about-author',
          title: this.$t('MetaTabsBlock.tabs.author.label'),
          shortTitle: this.$t('MetaTabsBlock.tabs.author.short.label')
        });
      }
      if (this.bookSeo.FAQ?.length) {
        const faqCount = this.bookSeo.FAQ.length;
        tabs.push({
          id: 'faq',
          title: `${this.$t('MetaTabsBlock.tabs.faq.label')} (${faqCount})`,
          shortTitle: `${this.$t(
            'MetaTabsBlock.tabs.faq.short.label'
          )} (${faqCount})`
        });
      }
      return tabs;
    },
    isButtonsDisabled() {
      return this.$store.getters['PlaybackStore/getIsTriggeredFromMeta'];
    }
  },
  mounted() {
    this.activeTab = 'book';

    this.$emit('tabChanged', {
      activeTab: this.activeTab,
      isBookTab: this.activeTab === 'book'
    });

    this.$nextTick(() => {
      this.updateUnderlinePosition();
    });

    window.addEventListener('resize', this.updateUnderlinePosition);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateUnderlinePosition);
  },
  methods: {
    handleTabChange(tabValue) {
      if (this.activeTab === 'book' && tabValue !== 'book') {
        this.stopAudioPlayback();
      }

      this.activeTab = tabValue;

      this.$emit('tabChanged', {
        activeTab: tabValue,
        isBookTab: tabValue === 'book'
      });

      this.$nextTick(() => {
        this.updateUnderlinePosition();
      });
    },

    stopAudioPlayback() {
      try {
        this.$store.dispatch('PlaybackStore/pause');
      } catch (error) {
        logger.warn('Failed to stop playback via PlaybackStore:', error);
      }
    },

    updateUnderlinePosition() {
      const activeButton = this.$refs[`tab-${this.activeTab}`];
      if (activeButton && activeButton[0]) {
        const button = activeButton[0];
        const buttonRect = button.getBoundingClientRect();
        const containerRect = this.$refs.tabButtons.getBoundingClientRect();

        this.underlineStyle = {
          width: `${buttonRect.width}px`,
          left: `${buttonRect.left - containerRect.left}px`
        };
      }
    }
  }
};
</script>

<style lang="less">
.book-meta-tabs {
  width: 100%;

  .book-block {
    display: none;
  }

  .book-meta-tabs-controls {
    .tab-buttons {
      display: flex;
      gap: 16px;
      position: relative;
      padding-bottom: 4px;
    }

    .tab-button {
      background: none;
      border: none;
      padding: 12px 0;
      min-width: 0;
      text-transform: none;
      font-size: 16px;
      cursor: pointer;
      color: inherit;
      transition: color 0.3s ease, opacity 0.3s ease;

      &:hover {
        opacity: 0.8;
      }

      &--active {
        color: var(--primary-color);
      }

      .default-theme-template &:not(.tab-button--active),
      .sepia-theme-template &:not(.tab-button--active) {
        color: #0f172a;
      }
    }

    .tab-underline {
      position: absolute;
      bottom: 0;
      height: 4px;
      background-color: var(--primary-color);
      border-radius: 2px;
      transition: all 0.3s ease;
      transform-origin: left;
    }
  }

  .tab-content {
    &:not(.book-block) {
      margin-bottom: 50px;
    }

    ol,
    ul {
      li {
        display: list-item;
        margin: 0 0 0.5em;
        font-size: 18px !important;
      }
    }

    ol {
      li {
        list-style-position: inside;
        margin-inline-start: 1.2em;
      }
    }

    ul {
      li {
        list-style-type: disc;
        margin-inline-start: 2.4em;
      }
    }
  }

  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding: 0 !important;
    line-height: 1.6 !important;
    margin-bottom: 1em !important;
  }

  p {
    font-size: 18px !important;
  }
}

.tab-content-placeholder {
  padding: 20px 0;

  .faq-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
    color: var(--v-text-base);
  }

  .faq-content {
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 12px;
    color: var(--v-text-base);
  }

  .v-expansion-panel {
    background-color: transparent !important;

    + .v-expansion-panel {
      border-top: 1px solid #e2e8f0;

      .sepia-theme-template & {
        border-top: 1px solid #a8a5a0;
      }
    }
  }

  .v-expansion-panel--active {
    &:not(:first-child),
    + .v-expansion-panel {
      margin-top: 0;
    }
  }

  .v-expansion-panel-header {
    padding: 0;
    font-weight: 700;
    font-size: 18px;
    line-height: 26px;
    min-height: 64px;
  }

  .v-expansion-panel-content__wrap {
    padding: 0 0 16px;
    font-size: 16px;
    line-height: 24px;
  }

  .v-expansion-panel-header__icon {
    svg {
      fill: currentColor;

      .default-theme-template &,
      .sepia-theme-template & {
        fill: #64748b;
      }
    }
  }
}

.faq-item {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  &:last-child {
    border-bottom: none;
  }
}

// Dark theme support
.theme--dark {
  .faq-item {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
}
</style>
